import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { getProject, projects } from "@/data/projects";
import { MaskLines, Reveal } from "@/components/Motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProject(slug);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const closeButtonRef = useRef(null);
  const gallery = project?.gallery ?? [];
  const hasMultipleImages = gallery.length > 1;
  const isLightboxOpen = activeImageIndex !== null;

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const previouslyFocusedElement = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveImageIndex(null);
      } else if (hasMultipleImages && event.key === "ArrowLeft") {
        setActiveImageIndex((current) =>
          current === null ? null : (current - 1 + gallery.length) % gallery.length
        );
      } else if (hasMultipleImages && event.key === "ArrowRight") {
        setActiveImageIndex((current) =>
          current === null ? null : (current + 1) % gallery.length
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      previouslyFocusedElement?.focus();
    };
  }, [gallery.length, hasMultipleImages, isLightboxOpen]);

  if (!project) {
    return (
      <div className="min-h-screen bg-offwhite grid place-items-center px-6">
        <div className="text-center">
          <p className="font-heading text-3xl text-charcoal mb-6">Project niet gevonden</p>
          <Link to="/" className="text-forest link-underline" data-testid="back-home">Terug naar home</Link>
        </div>
      </div>
    );
  }

  const others = projects.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="bg-white">
      <Navbar />

      <section className="relative h-[80svh] min-h-[520px] w-full overflow-hidden bg-charcoal">
        <motion.img
          src={project.cover}
          alt={`${project.name} — ${project.type} in ${project.location}`}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/10 to-charcoal/30" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 h-full flex flex-col justify-end pb-14 md:pb-20">
          <button
            data-testid="back-to-realisaties"
            onClick={() => navigate("/#realisaties")}
            className="inline-flex items-center gap-2 text-cream/80 font-body text-sm mb-8 link-underline w-fit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-4 focus-visible:ring-offset-ink"
          >
            <ArrowLeft size={16} /> Alle realisaties
          </button>
          <p className="font-body text-cream/80 text-sm tracking-[0.25em] uppercase mb-4">
            {project.location} · {project.type}
          </p>
          <h1 className="font-heading font-light tracking-tight text-cream text-4xl md:text-7xl leading-[1.02]">
            <MaskLines lines={[project.name]} delay={0.2} testId="project-title" />
          </h1>
        </div>
      </section>

      <section className="py-24 md:py-32 lg:py-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-heading font-light text-2xl md:text-3xl leading-snug text-ink text-balance mb-8 tracking-tight">
                {project.intro}
              </p>
              <p className="font-body text-slate2 leading-relaxed text-lg">
                {project.description}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.1}>
              <dl className="border-t border-stone">
                {project.facts.map((f) => (
                  <div key={f.label} className="flex justify-between py-4 border-b border-stone">
                    <dt className="font-body text-sm text-slate2">{f.label}</dt>
                    <dd className="font-body text-sm text-ink">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32 lg:pb-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 space-y-4 md:space-y-6">
          {project.gallery.map((image, i) => (
            <Reveal key={image.src} delay={0.05 * i}>
              <button
                type="button"
                onClick={() => setActiveImageIndex(i)}
                aria-label={`Open afbeelding ${i + 1} van ${project.name}`}
                data-testid={`gallery-image-${i}`}
                className="relative block w-full overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[16/10] shadow-[0_18px_50px_rgba(45,51,47,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-4"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  style={{ objectPosition: image.objectPosition }}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {activeImageIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Afbeeldingengalerij van ${project.name}`}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/95 p-4 md:p-8"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveImageIndex(null);
          }}
        >
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setActiveImageIndex(null)}
            aria-label="Galerij sluiten"
            data-testid="lightbox-close"
            className="absolute right-4 top-4 z-20 grid h-11 w-11 place-items-center rounded-full border border-white/40 bg-ink/40 text-white transition-colors hover:bg-ink/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-8 md:top-8"
          >
            <X size={22} />
          </button>

          <div className="flex max-h-full w-full max-w-6xl flex-col items-center gap-4">
            <div className="relative flex max-h-[calc(100svh-6rem)] w-full items-center justify-center">
              <img
                src={gallery[activeImageIndex].src}
                alt={gallery[activeImageIndex].alt}
                data-testid="lightbox-image"
                className="max-h-[calc(100svh-6rem)] max-w-full rounded-xl object-contain shadow-2xl md:rounded-2xl"
              />

              {hasMultipleImages && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveImageIndex(
                        (activeImageIndex - 1 + gallery.length) % gallery.length
                      )
                    }
                    aria-label="Vorige afbeelding"
                    data-testid="lightbox-previous"
                    className="absolute left-2 grid h-11 w-11 place-items-center rounded-full border border-white/40 bg-ink/60 text-white transition-colors hover:bg-ink/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:left-4 md:h-12 md:w-12"
                  >
                    <ArrowLeft size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveImageIndex((activeImageIndex + 1) % gallery.length)
                    }
                    aria-label="Volgende afbeelding"
                    data-testid="lightbox-next"
                    className="absolute right-2 grid h-11 w-11 place-items-center rounded-full border border-white/40 bg-ink/60 text-white transition-colors hover:bg-ink/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-4 md:h-12 md:w-12"
                  >
                    <ArrowRight size={22} />
                  </button>
                </>
              )}
            </div>

            {hasMultipleImages && (
              <p
                className="font-body text-sm tabular-nums text-cream/80"
                data-testid="lightbox-counter"
              >
                {activeImageIndex + 1} / {gallery.length}
              </p>
            )}
          </div>
        </div>
      )}

      <section className="bg-sand py-24 md:py-32 lg:py-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between mb-12">
            <h2 className="font-heading font-light text-3xl md:text-5xl tracking-tight leading-[1.1] text-ink text-balance">
              Andere realisaties
            </h2>
            <button
              onClick={() => navigate("/#realisaties")}
              data-testid="view-all-projects"
              className="hidden md:inline-flex items-center gap-2 rounded-cta border border-ink/15 bg-transparent px-6 py-3 text-ink font-body text-sm font-medium transition-all duration-300 hover:bg-offwhite hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest/50 focus-visible:ring-offset-2"
            >
              Bekijk alles <ArrowRight size={16} />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {others.map((p) => (
              <button
                key={p.slug}
                data-testid={`related-${p.slug}`}
                onClick={() => navigate(`/realisaties/${p.slug}`)}
                className="group text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-4 focus-visible:ring-offset-sand"
              >
                <div className="relative overflow-hidden rounded-2xl md:rounded-3xl aspect-[16/10] shadow-[0_16px_40px_rgba(45,51,47,0.08)] transition-shadow duration-500 group-hover:shadow-[0_22px_55px_rgba(45,51,47,0.13)]">
                  <img
                    src={p.cover}
                    alt={`${p.name} — ${p.type} in ${p.location}`}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6 flex items-center justify-between w-full">
                    <div>
                      <p className="font-body text-cream/80 text-xs tracking-widest uppercase">{p.location}</p>
                      <h3 className="font-heading text-cream text-2xl tracking-tight">{p.name}</h3>
                    </div>
                    <span className="h-11 w-11 shrink-0 rounded-full border border-white/50 bg-ink/10 backdrop-blur-sm grid place-items-center text-white opacity-100 transition-all duration-300 md:translate-y-2 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100">
                      <ArrowUpRight size={20} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
